import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import NamedLink from '../NamedLink/NamedLink';
import { useHistory } from 'react-router-dom';
import { onboardingCheer, celebrationFor, CHEER_TONE_LISTEN } from '../../util/onboardingCheer';

import css from './OnboardingCheer.module.css';

/**
 * One short, warm line at the top of a setup step (replaces "Step X of Y").
 */
export const CheerLine = props => {
  const { steps, current, role, className } = props;
  const cheer = onboardingCheer({ steps, current, role });
  if (!cheer) {
    return null;
  }
  return (
    <p
      className={classNames(
        css.cheer,
        cheer.tone === CHEER_TONE_LISTEN ? css.listen : css.talk,
        className
      )}
      data-testid="onboarding-cheer"
      data-cheer={cheer.key}
      role="status"
    >
      <span className={css.spark} aria-hidden="true">
        ✦
      </span>
      {cheer.text}
    </p>
  );
};

const COLORS_TALK = ['#F51F79', '#FF7AB3', '#FFC2DC', '#FFD166', '#159BE8'];
const COLORS_LISTEN = ['#159BE8', '#7CC8F5', '#C9E9FC', '#FFD166', '#F51F79'];

const prefersReducedMotion = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch (e) {
    return true;
  }
};

/**
 * Light canvas confetti, ~2.5s, no dependencies. Skipped for reduced motion.
 */
const Confetti = ({ colors }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || prefersReducedMotion() || !canvas.getContext) {
      return undefined;
    }
    let ctx = null;
    try {
      ctx = canvas.getContext('2d');
    } catch (e) {
      ctx = null;
    }
    if (!ctx) {
      return undefined;
    }
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    const pieces = Array.from({ length: 90 }, (_, i) => ({
      x: w / 2 + (Math.random() - 0.5) * 60,
      y: h * 0.35,
      vx: (Math.random() - 0.5) * 9,
      vy: -Math.random() * 9 - 4,
      r: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      s: 5 + Math.random() * 5,
      c: colors[i % colors.length],
    }));
    const start = performance.now();
    let raf;
    const tick = now => {
      const t = now - start;
      ctx.clearRect(0, 0, w, h);
      pieces.forEach(p => {
        p.vy += 0.22;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.r += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - t / 2600);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.s / 2, -p.s / 4, p.s, p.s / 2);
        ctx.restore();
      });
      if (t < 2600) {
        raf = window.requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, w, h);
      }
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [colors]);
  return <canvas ref={ref} className={css.confetti} aria-hidden="true" />;
};

/**
 * "You're all set" celebration with one clear next action.
 * Listeners (and Both) → Available now on the dashboard; talkers → Browse listeners.
 */
export const OnboardingCelebration = props => {
  const { role, onAction, asOverlay = false } = props;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Sam 8:23 PM Oct 23: finishing setup mid-booking (paid or free) goes straight back to that checkout.
  const history = useHistory();
  useEffect(() => {
    if (typeof window === 'undefined' || !/CheckoutPage/.test(window.location.search || '')) return;
    try {
      const r = JSON.parse(window.localStorage.getItem('sl_resume_checkout') || 'null');
      if (r?.path && Date.now() - r.at < 3 * 60 * 60 * 1000) {
        history ? history.replace(r.path) : window.location.replace(r.path);
        return;
      }
    } catch (e) {}
    for (const store of [window.sessionStorage, window.localStorage]) {
      try {
        const d = JSON.parse(store.getItem('CheckoutPage') || 'null');
        const id = d?.listing?.id?.uuid || d?.listing?.id;
        if (typeof id === 'string' && /^[0-9a-f-]{36}$/.test(id)) {
          const slug = String(d?.listing?.attributes?.title || 'listener').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'listener';
          window.location.replace(`/l/${slug}/${id}/checkout`);
          return;
        }
      } catch (e) {}
    }
  }, []);
  const copy = celebrationFor(role);
  const listen = role === 'listen' || role === 'both';
  const content = (
    <div
      className={classNames(css.celebration, listen ? css.listen : css.talk)}
      data-testid="onboarding-celebration"
      data-celebration-role={listen ? 'listen' : 'talk'}
    >
      {mounted ? <Confetti colors={listen ? COLORS_LISTEN : COLORS_TALK} /> : null}
      <div className={css.burst} aria-hidden="true">
        🎉
      </div>
      <h1 className={css.celebrationTitle}>{copy.title}</h1>
      <p className={css.celebrationBody}>{copy.body}</p>
      <NamedLink
        name={copy.ctaRoute}
        className={css.celebrationCta}
        onClick={onAction}
        data-celebration-cta={copy.ctaRoute}
      >
        {copy.cta}
      </NamedLink>
      {copy.secondary ? (
        <NamedLink name={copy.secondaryRoute} className={css.celebrationSecondary} onClick={onAction}>
          {copy.secondary}
        </NamedLink>
      ) : null}
    </div>
  );
  if (!asOverlay) {
    return content;
  }
  return (
    <div className={css.overlay} role="dialog" aria-modal="true" aria-label={copy.title}>
      <div className={css.overlayCard}>
        <button type="button" className={css.close} onClick={onAction} aria-label="Close">
          ×
        </button>
        {content}
      </div>
    </div>
  );
};

export default CheerLine;
